import { FormikValues } from 'formik';
import React, { Component, PropsWithChildren, createRef, ReactElement, ReactNode, RefObject } from 'react';
import { Button, Form, Icon, ModalFooter } from '../index';
import { IWizardStepPropsPrivate } from './types';
import { IWizardMetaProps } from './utils';

import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { fontAwesome } from '@hl/ui-components';
fontAwesome.library.add(faAngleLeft);

type WizardStepCallbackKey = '_onClose' | '_onNext' | '_onPrevious' | '_onSave';

export class WizardStep extends Component<PropsWithChildren<IWizardStepPropsPrivate>> {
  public customComponent: RefObject<any> = createRef<any>();

  private submitForm: any = undefined;

  public componentDidMount() {
    // Set this component as a reference on its parent component so that Wizard can trigger handleNextClick
    const { _onRef } = this.props;
    _onRef && _onRef(this);
  }

  public render() {
    const { children: child, type = 'static' } = this.props;

    if (type === 'form') {
      return this.renderForm();
    } else if (type === 'custom') {
      const childWithRef = React.cloneElement(child as ReactElement<any>, {
        ref: this.customComponent,
      });
      return this.renderStep(childWithRef);
    } else {
      return this.renderStep(child);
    }
  }

  public handleNextButton = () => {
    const { type } = this.props;

    if (type === 'form') {
      this.handleValidateForm();
    } else if (type === 'custom') {
      this.handleCustomValidation();
    } else if (type === 'static') {
      this.handleStaticNext();
    }
  };

  private modalFooter() {
    const { isFirst, isLast, node } = this.props._meta as IWizardMetaProps;
    const {
      nextLabel = isLast ? 'Finish' : 'Continue',
      previousLabel = 'Back',
      saveLabel = 'Save as draft',
    } = node.props;

    const maybeBackButton = () => {
      if (isFirst) {
        return null;
      }
      return (
        <button
          type="button"
          className="btn btn-link pull-left"
          onClick={(e) => {
            this.handlePrevious(e);
          }}
        >
          <Icon icon={'angle-left'} /> {previousLabel}
        </button>
      );
    };

    const maybeSaveButton = () => {
      return this.props._onSave ? (
        <Button type="button" color="default" label={saveLabel} onClick={this.handleSave} />
      ) : null;
    };

    return (
      <ModalFooter>
        {maybeBackButton()}
        <Button type="button" label="Cancel" color="default" onClick={this.handleClose} />
        {maybeSaveButton()}
        <Button label={nextLabel} type="button" color="primary" onClick={this.handleNextButton} />
      </ModalFooter>
    );
  }

  private handleBeforeTransition = (values: FormikValues) => {
    const { _beforeTransition } = this.props;
    _beforeTransition && _beforeTransition(values);
  };

  private handleButtonClick(e: React.SyntheticEvent<HTMLButtonElement>, callback: WizardStepCallbackKey) {
    e.preventDefault();
    const fn = (this.props as IWizardStepPropsPrivate)[callback];
    if (typeof fn === 'function') {
      (fn as () => void)();
    }
  }

  private handleClose = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    this.handleButtonClick(e, '_onClose');
  };

  private handleValidateForm = () => {
    this.submitForm();
  };

  private handleCustomValidation = () => {
    const values = this.customComponent.current.validate();
    if (values) {
      this.props._onComplete && this.props._onComplete();
      this.handleBeforeTransition(values);
    }
  };

  private handleSubmit = (values: FormikValues) => {
    this.props._onComplete && this.props._onComplete();
    this.handleBeforeTransition(values);
  };

  private handleStaticNext = () => {
    const { _onComplete, _onNext } = this.props;
    _onComplete && _onComplete();

    _onNext && _onNext({});
  };

  private handlePrevious = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    this.handleButtonClick(e, '_onPrevious');
  };

  private handleSave = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    this.handleButtonClick(e, '_onSave');
  };

  private renderForm() {
    const { children: form, id } = this.props;

    const { render, onSubmit = this.handleSubmit, ...formProps } = (React.Children.only(form) as ReactElement).props;

    return (
      <Form
        enableReinitialize={true}
        key={id}
        render={(props) => {
          this.submitForm = props.submitForm;
          return this.renderStep(render(props));
        }}
        {...formProps}
        onSubmit={this.handleSubmit}
      />
    );
  }

  // tslint:disable-next-line
  private renderStep(child: ReactElement<any> | ReactNode) {
    return (
      <div className={'hlc-wizard-step'} role={'tabpanel'}>
        <div className={'hlc-wizard-body'}>{child}</div>
        {this.modalFooter()}
      </div>
    );
  }
}

export default WizardStep;
